/**
 * SpecRecommendationsPanel Component
 *
 * Displays AI-generated recommendations for a specification.
 * Allows acknowledging, implementing, or dismissing recommendations.
 *
 * Part of PowerSpec V3 - CPO Dashboard Integration
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Spinner, Alert, Modal } from 'flowbite-react';
import {
  HiLightBulb,
  HiCurrencyDollar,
  HiSparkles,
  HiShieldCheck,
  HiLightningBolt,
  HiCheck,
  HiX,
  HiExclamation,
  HiRefresh
} from 'react-icons/hi';
import {
  getSpecRecommendations,
  acknowledgeRecommendation,
  getPriorityColor,
  getRecommendationStatusColor,
  formatCost,
  type SpecRecommendation
} from '../api/specifications';
import { isSuccessResponse } from '../api/apiClient';

interface SpecRecommendationsPanelProps {
  specificationId: string;
  onError?: (error: string) => void;
}

export const SpecRecommendationsPanel: React.FC<SpecRecommendationsPanelProps> = ({
  specificationId,
  onError
}) => {
  const [recommendations, setRecommendations] = useState<SpecRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRec, setSelectedRec] = useState<SpecRecommendation | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSpecRecommendations(specificationId);

      if (isSuccessResponse(response)) {
        setRecommendations(response.data?.Records || []);
      } else {
        throw new Error(response.message || 'Failed to load recommendations');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load recommendations';
      console.error('[SpecRecommendationsPanel] Error:', err);
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [specificationId, onError]);

  useEffect(() => {
    if (specificationId) {
      fetchData();
    }
  }, [specificationId, fetchData]);

  const handleAction = async (
    recommendationId: string,
    status: 'acknowledged' | 'implemented' | 'dismissed'
  ) => {
    try {
      setActionLoading(recommendationId);

      const response = await acknowledgeRecommendation(recommendationId, status);

      if (isSuccessResponse(response)) {
        // Update the local state
        setRecommendations(prev =>
          prev.map(rec =>
            rec.Recommendation_ID === recommendationId
              ? { ...rec, Status: status, Acknowledged_At: new Date().toISOString() }
              : rec
          )
        );
        setShowModal(false);
        setSelectedRec(null);
      } else {
        throw new Error(response.message || 'Failed to update recommendation');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update recommendation';
      console.error('[SpecRecommendationsPanel] Action error:', err);
      onError?.(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cost_optimization':
        return <HiCurrencyDollar className="w-5 h-5 text-green-600" />;
      case 'quality_improvement':
        return <HiSparkles className="w-5 h-5 text-purple-600" />;
      case 'security':
        return <HiShieldCheck className="w-5 h-5 text-red-600" />;
      case 'performance':
        return <HiLightningBolt className="w-5 h-5 text-yellow-600" />;
      default:
        return <HiLightBulb className="w-5 h-5 text-blue-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const pendingRecs = recommendations.filter(r => r.Status === 'pending');
  const acknowledgedRecs = recommendations.filter(r => r.Status === 'acknowledged');
  const resolvedRecs = recommendations.filter(r => ['implemented', 'dismissed'].includes(r.Status));
  const totalSavings = recommendations
    .filter(r => r.Status !== 'dismissed' && r.Potential_Savings)
    .reduce((sum, r) => sum + (r.Potential_Savings || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading recommendations</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            className="mt-2 text-sm underline"
            onClick={fetchData}
          >
            Retry
          </button>
        </div>
      </Alert>
    );
  }

  const renderRecommendationCard = (rec: SpecRecommendation) => (
    <Card
      key={rec.Recommendation_ID}
      className={`border-l-4 ${
        rec.Priority === 'high' ? 'border-red-500' :
        rec.Priority === 'medium' ? 'border-yellow-500' : 'border-blue-500'
      }`}
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {getTypeIcon(rec.Recommendation_Type)}
            <div>
              <h4 className="font-semibold text-gray-900">{rec.Title}</h4>
              <div className="flex items-center gap-2 mt-1">
                <Badge color={getPriorityColor(rec.Priority)}>
                  {rec.Priority.toUpperCase()}
                </Badge>
                <Badge color={getRecommendationStatusColor(rec.Status)}>
                  {rec.Status.replace('_', ' ').toUpperCase()}
                </Badge>
                <span className="text-xs text-gray-500">
                  {rec.Recommendation_Type.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          {rec.Potential_Savings && rec.Potential_Savings > 0 && (
            <div className="text-right">
              <div className="text-xs text-gray-500">Potential Savings</div>
              <div className="text-lg font-bold text-green-600">
                {formatCost(rec.Potential_Savings)}
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm">{rec.Description}</p>

        {/* Estimated Impact */}
        {rec.Estimated_Impact && (
          <div className="p-2 bg-gray-50 rounded text-sm text-gray-700">
            <span className="font-medium">Impact: </span>
            {rec.Estimated_Impact}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            Created: {formatDate(rec.Create_Date)}
            {rec.Acknowledged_At && ` | Acknowledged: ${formatDate(rec.Acknowledged_At)}`}
          </span>

          {rec.Status === 'pending' && (
            <div className="flex gap-2">
              <Button
                size="xs"
                color="success"
                onClick={() => {
                  setSelectedRec(rec);
                  setShowModal(true);
                }}
                disabled={actionLoading === rec.Recommendation_ID}
              >
                {actionLoading === rec.Recommendation_ID ? (
                  <Spinner size="xs" />
                ) : (
                  <>
                    <HiCheck className="w-4 h-4 mr-1" />
                    Acknowledge
                  </>
                )}
              </Button>
              <Button
                size="xs"
                color="gray"
                onClick={() => handleAction(rec.Recommendation_ID, 'dismissed')}
                disabled={actionLoading === rec.Recommendation_ID}
              >
                <HiX className="w-4 h-4 mr-1" />
                Dismiss
              </Button>
            </div>
          )}

          {rec.Status === 'acknowledged' && (
            <Button
              size="xs"
              color="success"
              onClick={() => handleAction(rec.Recommendation_ID, 'implemented')}
              disabled={actionLoading === rec.Recommendation_ID}
            >
              {actionLoading === rec.Recommendation_ID ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <HiCheck className="w-4 h-4 mr-1" />
                  Mark Implemented
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <HiLightBulb className="w-5 h-5 text-yellow-500" />
          AI Recommendations
        </h3>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
        >
          <HiRefresh className="w-4 h-4" />
        </button>
      </div>

      {/* Summary Stats */}
      {recommendations.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-yellow-50">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{pendingRecs.length}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
          </Card>
          <Card className="bg-blue-50">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{acknowledgedRecs.length}</div>
              <div className="text-sm text-gray-600">Acknowledged</div>
            </div>
          </Card>
          <Card className="bg-green-50">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{resolvedRecs.length}</div>
              <div className="text-sm text-gray-600">Resolved</div>
            </div>
          </Card>
          <Card className="bg-purple-50">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{formatCost(totalSavings)}</div>
              <div className="text-sm text-gray-600">Potential Savings</div>
            </div>
          </Card>
        </div>
      )}

      {/* No Recommendations */}
      {recommendations.length === 0 && (
        <Card>
          <div className="text-center py-8">
            <HiLightBulb className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No recommendations available for this specification</p>
            <p className="text-sm text-gray-400 mt-2">
              Recommendations are generated based on execution patterns and cost analysis
            </p>
          </div>
        </Card>
      )}

      {/* Pending Recommendations */}
      {pendingRecs.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <HiExclamation className="w-4 h-4 text-yellow-500" />
            Pending Review ({pendingRecs.length})
          </h4>
          {pendingRecs.map(renderRecommendationCard)}
        </div>
      )}

      {/* Acknowledged Recommendations */}
      {acknowledgedRecs.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-700">
            In Progress ({acknowledgedRecs.length})
          </h4>
          {acknowledgedRecs.map(renderRecommendationCard)}
        </div>
      )}

      {/* Resolved Recommendations */}
      {resolvedRecs.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-700">
            Resolved ({resolvedRecs.length})
          </h4>
          {resolvedRecs.map(renderRecommendationCard)}
        </div>
      )}

      {/* Action Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>Acknowledge Recommendation</Modal.Header>
        <Modal.Body>
          {selectedRec && (
            <div className="space-y-4">
              <p className="text-gray-600">
                You are acknowledging the following recommendation:
              </p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold">{selectedRec.Title}</h4>
                <p className="text-sm text-gray-600 mt-2">{selectedRec.Description}</p>
                {selectedRec.Potential_Savings && (
                  <p className="text-sm text-green-600 mt-2">
                    Potential savings: {formatCost(selectedRec.Potential_Savings)}
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-500">
                Acknowledging means you will review and potentially implement this recommendation.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={() => selectedRec && handleAction(selectedRec.Recommendation_ID, 'acknowledged')}
            disabled={actionLoading !== null}
          >
            {actionLoading ? <Spinner size="sm" /> : 'Acknowledge'}
          </Button>
          <Button
            color="gray"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SpecRecommendationsPanel;
